// run-command-prompt-previous-rejection-section.ts
//
// Phase 06d R6 (dogfood F-16, spec E19): a retried attempt used to get the
// exact same prompt as the one the controller had just refused, so the worker
// repeated the same mistake. From attempt 2 on, the prompt carries WHY the
// previous attempt ended — `category` + `reasons` of `ticket_last_verdict`
// (mission-state-reducer.ts). `raw_result_tail` stays out (worker prose /
// large output, N3).
//
// Phase 06e R1 (dogfood F-24, spec E22): after a SCHEMA rejection the reason
// alone ("invalid_structured_output_schema") never said which field was
// wrong, so the worker rewrote the whole result and dropped proposed_changes.
// Schema mode swaps the lead line and adds the failing FIELD PATHS from
// `schema_error_detail` — only paths that pass a field-name whitelist (zod
// paths are schema keys, never values; the whitelist is defence in depth),
// ≤ 5 of them, known-values masked like everything else here.
//
// Redaction (B13): the reducer only pattern-redacts, and `MissionStore.commit`
// masks known values with the registry AS OF the commit — a secret registered
// later is still raw in state. So the caller passes the CURRENT known values
// and this module masks them at render time, THEN caps the body (masking after
// a cut could leave a partial secret the matcher no longer sees).
//
// Pure + sync: the caller (supervisor-loop.ts) does the one async registry read.
import type { MissionState } from "./mission-state-types.js";
import { redactKnownValues } from "./secret-redactor.js";

export const PREVIOUS_REJECTION_SECTION_TITLE = "## Lần trước bị từ chối vì";
/** F-56 (run 11): a signal-stopped attempt was NOT refused — own title, no "sửa lý do" lead. */
export const PREVIOUS_INTERRUPTED_SECTION_TITLE = "## Attempt trước bị ngắt (không bị từ chối)";
const INTERRUPTED_LEAD =
  "- Attempt trước dừng vì người vận hành gửi tín hiệu (Ctrl-C/SIGTERM), controller KHÔNG từ chối kết quả nào. Thay đổi dở của nó đã được reset (bản sao ở forensics) — làm ticket này lại như bình thường.";
export const PREVIOUS_REJECTION_SECTION_MAX_CHARS = 1200;

/** One prompt line per value: a reason never opens a new line/heading of its own (audit F-2: any control/line/paragraph separator, `\v`/`\f`/NEL included). */
function oneLine(text: string): string {
  return text.replace(/[\p{Cc}\p{Zl}\p{Zp}]+/gu, " ").trim();
}

const SCHEMA_REASONS = ["invalid_structured_output_schema", "no_result_json_found"];
const SCHEMA_LEAD =
  '- Result JSON của attempt trước sai schema. Chỉ sửa đúng các trường nêu dưới; giữ nguyên mọi phần đúng còn lại (proposed_changes, decisions, …). Mảng không có gì để khai ⇒ [] (không dùng giá trị giả như "/dev/null").';
const DEFAULT_LEAD =
  "- Controller đã từ chối attempt trước của ticket này. Sửa đúng các lý do dưới đây, không lặp lại cách làm cũ.";
const SCHEMA_FIELD_PATH_RE =
  /^[a-z_][a-z0-9_]{0,63}(\[\d{1,4}\])?(\.[a-z_][a-z0-9_]{0,63}(\[\d{1,4}\])?)*$/;
const SCHEMA_FIELD_PATH_MAX_CHARS = 120;
const SCHEMA_FIELD_PATHS_SHOWN = 5;

/**
 * Field paths of a `schema_error_detail` (`invalid: <p1>, <p2>` —
 * formatSchemaErrorDetail) that may enter a prompt: whitelisted field-name
 * shape only, each ≤ 120 chars. Everything else is dropped, never echoed.
 */
export function schemaFieldPathsForRetryPrompt(detail: string | undefined): string[] {
  if (detail === undefined) return [];
  const body = detail.startsWith("invalid: ") ? detail.slice("invalid: ".length) : detail;
  return body
    .split(", ")
    .map((path) => path.trim())
    .filter(
      (path) => path.length <= SCHEMA_FIELD_PATH_MAX_CHARS && SCHEMA_FIELD_PATH_RE.test(path),
    );
}

function capped(body: string): string {
  if (body.length <= PREVIOUS_REJECTION_SECTION_MAX_CHARS) return body;
  return `${body.slice(0, PREVIOUS_REJECTION_SECTION_MAX_CHARS - 1)}…`;
}

/**
 * `""` for attempt 1, for a ticket with no verdict yet, and after a
 * `needs_decision` (not a failure — the decided-questions section covers it),
 * so those prompts stay byte-identical (golden I1). Otherwise a heading plus
 * `- category: …` and one `- <reason>` line per reason, body ≤ 1 200 chars;
 * a schema verdict adds `- trường sai schema: …` (E22), other verdicts are
 * byte-identical to 06d.
 * A quota attempt journals no new verdict, so the section keeps showing the
 * last real one (N4 vòng 2, accepted).
 */
export function renderPreviousRejectionSection(
  state: Pick<MissionState, "ticket_last_verdict">,
  ticketId: string,
  attemptNo: number,
  knownValues: readonly string[],
): string {
  if (attemptNo < 2) return "";
  const verdict = state.ticket_last_verdict[ticketId];
  if (verdict === undefined || verdict.category === "needs_decision") return "";
  if (verdict.category === "interrupted") {
    return ["", PREVIOUS_INTERRUPTED_SECTION_TITLE, INTERRUPTED_LEAD].join("\n");
  }
  const mask = (text: string) => oneLine(redactKnownValues(text, knownValues));
  // Prefix, never `includes` (review L4): a 6f reason embeds a worker-chosen
  // target. `;` is the `"; stderr_tail: …"` suffix session-failure-reasons.ts adds.
  const schemaMode = verdict.reasons.some((r) =>
    SCHEMA_REASONS.some((s) => r === s || r.startsWith(`${s}:`) || r.startsWith(`${s};`)),
  );
  const paths = schemaMode ? schemaFieldPathsForRetryPrompt(verdict.schema_error_detail) : [];
  const shown = paths.slice(0, SCHEMA_FIELD_PATHS_SHOWN).join(", ");
  const more = paths.length > SCHEMA_FIELD_PATHS_SHOWN ? ", …" : "";
  const lines = [
    schemaMode ? SCHEMA_LEAD : DEFAULT_LEAD,
    // Right after the lead: a long reason (stderr tail) must not cap it away.
    ...(paths.length > 0 ? [`- trường sai schema: ${mask(shown)}${more}`] : []),
    `- category: ${mask(verdict.category)}`,
    ...verdict.reasons.map((reason) => `- ${mask(reason)}`),
  ];
  return ["", PREVIOUS_REJECTION_SECTION_TITLE, capped(lines.join("\n"))].join("\n");
}
